// FLAG:useDocuAi — retry policy for the receipt upload path. Pure/leaf so the backoff maths is
// unit-testable without timers or the store.
//
// Only reached for source 'expense' | 'revenue'; the legacy path has no retry at all and keeps
// failing terminally on the first attempt, exactly as before.

import { classifyUploadError } from '@/redux/ducks/document/_helpers/classifyUploadError'

// 3 attempts total = the initial try plus 2 retries. Deliberately small: the failure this plan
// targets is congestion, and a large retry budget under congestion is indistinguishable from a
// load test against our own API.
export const MAX_UPLOAD_ATTEMPTS = 3

const BASE_DELAY_MS = 500
const MAX_DELAY_MS = 8000

/**
 * Exponential backoff with FULL jitter: delay = random(0, min(max, base * 2^attempt)).
 *
 * The jitter is not decoration. A mass upload fails as a correlated burst — without jitter every
 * failed file waits the identical delay and they all retry in the same millisecond, reproducing
 * the exact thundering herd that caused the failures. Full jitter (rather than equal jitter)
 * spreads the retries across the whole window.
 *
 * `attempt` is 0-based: 0 = the delay before the first retry.
 * `random` is injectable so tests are deterministic.
 */
export const getRetryDelay = (
  attempt: number,
  { baseMs = BASE_DELAY_MS, maxMs = MAX_DELAY_MS, random = Math.random }: {
    baseMs?: number,
    maxMs?: number,
    random?: () => number,
  } = {},
): number => {
  const ceiling = Math.min(maxMs, baseMs * Math.pow(2, Math.max(0, attempt)))
  return Math.floor(random() * ceiling)
}

/**
 * Whether another attempt is allowed for this failure.
 *
 * `attemptsMade` counts attempts already completed (1 after the first failure).
 */
export const shouldRetryUpload = (error: unknown, attemptsMade: number, maxAttempts = MAX_UPLOAD_ATTEMPTS): boolean => {
  if (attemptsMade >= maxAttempts) return false
  return classifyUploadError(error) === 'transient'
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Run `task` with bounded retries. Resolves with the task's value, or rejects with the LAST error
 * once the budget is exhausted or the failure is permanent — so the caller's existing error
 * handling still sees a real error object and renders the real reason.
 *
 * `onAttemptFailed` reports a failure that WILL be retried, so the UI can show the current attempt
 * instead of an unexplained pause.
 */
export const withUploadRetry = async <T>(
  task: () => Promise<T>,
  {
    maxAttempts = MAX_UPLOAD_ATTEMPTS,
    onAttemptFailed,
    random,
  }: {
    maxAttempts?: number,
    onAttemptFailed?: (info: { attempt: number, maxAttempts: number, delayMs: number, error: unknown }) => void,
    random?: () => number,
  } = {},
): Promise<T> => {
  let attemptsMade = 0

  for (;;) {
    try {
      return await task()
    } catch (error) {
      attemptsMade += 1
      if (!shouldRetryUpload(error, attemptsMade, maxAttempts)) {
        throw error
      }
      const delayMs = getRetryDelay(attemptsMade - 1, { random })
      onAttemptFailed?.({ attempt: attemptsMade, maxAttempts, delayMs, error })
      await wait(delayMs)
    }
  }
}
